import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React from "react";

import { Currency } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { Redirect } from "src/components/Redirect";
import { TabHeader } from "src/components/TabHeader";
import {
  DefaultPageLayout,
  DefaultPageLayoutProps,
} from "src/components/layout/DefaultPageLayout";
import { HeaderTitlePageSection } from "src/components/layout/PageSection";
import { useShowNps } from "src/context/NpsContext/hooks";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  spacing,
  horizontalStackCss,
  verticalStackCss,
} from "src/theme/spacing";
import {
  FontWeight,
  secondaryMetadataTextCss,
  textSizeCss,
  TextSize,
} from "src/theme/text";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";

const TabHeaderStyled = styled(TabHeader)`
  ${cssForMediaSize({
    max: MediaSize.SMALL,
    css: textSizeCss[TextSize.s],
  })}
`;

export enum ManagePageTab {
  Overview = "Overview",
  Recurring = "Recurring",
  Donations = "Donations",
  Payment = "Payment",
  Fundraisers = "Fundraisers",
  Achievements = "Achievements",
}

interface LayoutProps extends Omit<DefaultPageLayoutProps, "headerContent"> {
  selectedTab: ManagePageTab;
}

export const ManagePageLayout: React.FCC<LayoutProps> = ({
  selectedTab,
  children,
  ...rest
}) => {
  useShowNps();
  return (
    <DefaultPageLayout
      {...rest}
      npsModal
      headerContent={
        <HeaderTitlePageSection
          contentCss={[
            cssForMediaSize({
              max: MediaSize.MEDIUM_SMALL,
              css: verticalStackCss.m,
            }),
            cssForMediaSize({
              min: MediaSize.MEDIUM,
              css: css`
                ${horizontalStackCss.l};

                margin-top: -${spacing.l};
                > * {
                  margin-top: ${spacing.l};
                }

                justify-content: space-between;
                flex-wrap: wrap;
                align-items: center;
              `,
            }),
          ]}
        >
          <h1>My Giving</h1>
          <ul
            css={[
              horizontalStackCss.l,
              cssForMediaSize({
                max: MediaSize.MEDIUM,
                css: css`
                  ${horizontalStackCss.m}
                  flex-wrap: wrap;
                  > li {
                    padding-bottom: ${spacing.m};
                  }
                `,
              }),
            ]}
          >
            <li>
              <TabHeaderStyled
                data-tname={`${ClientRouteName.MY_GIVING_HISTORY}_tab`}
                to={getRoutePath({
                  format: URLFormat.RELATIVE,
                  name: ClientRouteName.MY_GIVING_HISTORY,
                })}
                selectedTab={selectedTab}
                currentTab={ManagePageTab.Donations}
                textSize={TextSize.l}
              >
                History
              </TabHeaderStyled>
            </li>
            <li>
              <TabHeaderStyled
                data-tname={`${ClientRouteName.MY_GIVING_RECURRING}_tab`}
                to={getRoutePath({
                  format: URLFormat.RELATIVE,
                  name: ClientRouteName.MY_GIVING_RECURRING,
                })}
                selectedTab={selectedTab}
                currentTab={ManagePageTab.Recurring}
                textSize={TextSize.l}
              >
                Recurring
              </TabHeaderStyled>
            </li>
            <li>
              <TabHeaderStyled
                data-tname={`${ClientRouteName.MY_GIVING_PAYMENT}_tab`}
                to={getRoutePath({
                  format: URLFormat.RELATIVE,
                  name: ClientRouteName.MY_GIVING_PAYMENT,
                })}
                selectedTab={selectedTab}
                currentTab={ManagePageTab.Payment}
                textSize={TextSize.l}
              >
                Payment
              </TabHeaderStyled>
            </li>
            <li>
              <TabHeaderStyled
                data-tname={`${ClientRouteName.MY_GIVING_FUNDRAISERS}_tab`}
                to={getRoutePath({
                  format: URLFormat.RELATIVE,
                  name: ClientRouteName.MY_GIVING_FUNDRAISERS,
                })}
                selectedTab={selectedTab}
                currentTab={ManagePageTab.Fundraisers}
                textSize={TextSize.l}
              >
                Fundraisers
              </TabHeaderStyled>
            </li>
            <li>
              <TabHeaderStyled
                data-tname={`${ClientRouteName.MY_GIVING_ACHIEVEMENTS}_tab`}
                to={getRoutePath({
                  format: URLFormat.RELATIVE,
                  name: ClientRouteName.MY_GIVING_ACHIEVEMENTS,
                })}
                selectedTab={selectedTab}
                currentTab={ManagePageTab.Achievements}
                textSize={TextSize.l}
              >
                Achievements
              </TabHeaderStyled>
            </li>
          </ul>
        </HeaderTitlePageSection>
      }
    >
      {children}
    </DefaultPageLayout>
  );
};

export default function ManagePage() {
  return (
    <Redirect
      to={getRoutePath({
        format: URLFormat.RELATIVE,
        name: ClientRouteName.MY_GIVING_HISTORY,
      })}
      push={false}
    />
  );
}

// TODO #8478: parametrize currency
/**
 * Section header component for Manage pages.
 */
export const ManageSectionHeader: React.FCC<{
  className?: string;
  title: string;
  isLoaded?: boolean;
  amountDescription?: string;
  amount?: Big;
  amountPostfix?: string;
}> = ({
  className,
  title,
  amountDescription,
  amount,
  amountPostfix,
  isLoaded,
}) => (
  <header
    className={className}
    css={css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 ${spacing.m};
    `}
  >
    <h3 css={secondaryMetadataTextCss}>{title}</h3>
    {isLoaded && amount && (
      <dl
        css={[
          horizontalStackCss.xs,
          css`
            align-items: baseline;
          `,
        ]}
      >
        {amountDescription && (
          <dt
            css={css`
              color: var(${colorCssVars.text.secondary});
            `}
          >
            {amountDescription}:
          </dt>
        )}
        <b
          css={css`
            font-weight: ${FontWeight.BOLD};
            color: var(${colorCssVars.text.body});
            ${textSizeCss[TextSize.m]};
          `}
        >
          {displayCurrencyValueInUserLocale({
            // TODO #8478: parametrize currency
            currencyValue: { amount, currency: Currency.USD },
          })}
        </b>
        {amountPostfix && (
          <dt
            css={css`
              color: var(${colorCssVars.text.secondary});
            `}
          >
            {amountPostfix}
          </dt>
        )}
      </dl>
    )}
  </header>
);
